import { useCallback, useEffect, useRef, useState } from "react";
import { getWatchlist } from "../../../services/clearing/station-performance";
import type { ReportingOptions, WatchlistReport } from "../../../types/stationPerformance";

interface UseWatchlistArgs {
  asOf: string | null;
  options: ReportingOptions;
  flaggedOnly: boolean;
  threshold: number | null;
}

/** The pace-adjusted watchlist. `asOf` is optional; the server defaults it to today. */
export const useWatchlist = ({ asOf, options, flaggedOnly, threshold }: UseWatchlistArgs) => {
  const [report, setReport] = useState<WatchlistReport | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [neverComputed, setNeverComputed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // asOf and the filters can change while requests are in flight: only the newest load may write state.
  const requestId = useRef(0);

  const fetchWatchlist = useCallback(async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    setNeverComputed(null);
    setError(null);
    let result;
    try {
      result = await getWatchlist({ asOf, options, flaggedOnly, threshold });
    } catch {
      if (id !== requestId.current) return;
      setError("Could not load the watchlist.");
      setInitialLoading(false);
      setRefreshing(false);
      return;
    }
    if (id !== requestId.current) return;
    if (result?.success) {
      setReport(result.data ?? null);
    } else if (result?.neverComputed) {
      setNeverComputed(result.months ?? []);
      setReport(null);
    } else {
      setError(result?.message ?? "Could not load the watchlist.");
    }
    setInitialLoading(false);
    setRefreshing(false);
  }, [asOf, options, flaggedOnly, threshold]);

  useEffect(() => {
    fetchWatchlist();
    return () => {
      requestId.current += 1;
    };
  }, [fetchWatchlist]);

  return { report, initialLoading, refreshing, neverComputed, error, fetchWatchlist };
};
